import React, { useState } from 'react';
import { Loader2, CreditCard, RefreshCw, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { bapi, brandLabel, fmtDate, fmtMoney, startHostedPayment } from '@/lib/billingApi';
import { StatusPill } from './StatusPill';

const statusOf = (s) => {
  if (s.status === 'past_due') return { key: 'past_due', label: 'Payment failed' };
  if (s.source === 'complimentary') return { key: 'active', label: 'Complimentary' };
  return s.auto_renew ? { key: 'active', label: 'Active' } : { key: 'cancelled', label: 'Auto-renew off' };
};

const methodText = (s) => (s.source === 'paypal' ? 'PayPal' : s.source === 'complimentary' ? 'No payment method'
  : s.card_last4 ? `${brandLabel(s.card_brand)} •••• ${s.card_last4}` : 'Credit / Debit Card');

const Line = ({ label, value, testId }) => (
  <div><p className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">{label}</p><p className="text-sm font-semibold text-slate-800" data-testid={testId}>{value}</p></div>
);

export const SubscriptionCard = ({ sub, onChanged, cardEnabled = true }) => {
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState(false);
  const st = statusOf(sub);
  const t = `sub-${sub.entitlement}`;

  const act = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(e.message); setBusy(''); }
  };
  const toggle = (enabled) => act(enabled ? 'resume' : 'cancel', async () => {
    if (sub.source === 'paypal') await bapi('/officials/billing/cancel', { method: 'POST', body: {} });
    else await bapi(`/billing/subscriptions/${sub.id}/auto-renew`, { method: 'POST', body: { enabled } });
    toast.success(enabled ? 'Auto-renew is back on.' : 'Auto-renew turned off. You keep access until the end of your paid period.');
    setBusy(''); setConfirm(false); onChanged();
  });

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-300" data-testid={t}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-2xl font-black uppercase text-[#00247D] leading-none" style={{ fontFamily: 'Barlow Condensed' }}>{sub.product_name}</h3>
          <p className="text-xs text-slate-500 mt-1">{sub.billing_frequency}{sub.amount ? ` · ${fmtMoney(sub.amount, sub.currency)}` : ''}</p>
          {sub.includes && <p className="text-xs font-semibold text-slate-600 mt-1" data-testid={`${t}-includes`}>Includes {sub.includes.join(' + ')}</p>}
        </div>
        <StatusPill status={st.key} label={st.label} testId={`${t}-status`} />
      </div>
      {sub.status === 'past_due' && (
        <p className="mt-4 flex gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800" data-testid={`${t}-past-due`}>
          <AlertTriangle className="w-4 h-4 shrink-0" />Renewal failed{sub.last_error ? ` (${sub.last_error})` : ''}. Access continues until {fmtDate(sub.grace_until)}. Update your card or pay now.
        </p>
      )}
      {sub.action_required && sub.status !== 'past_due' && (
        <p className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900" data-testid={`${t}-action-required`}>Please confirm your card so your plan keeps renewing.</p>
      )}
      <div className="grid grid-cols-2 gap-4 mt-5">
        <Line label={sub.auto_renew ? 'Next renewal' : 'Access until'} value={fmtDate(sub.current_period_end)} testId={`${t}-period-end`} />
        <Line label="Payment method" value={methodText(sub)} testId={`${t}-method`} />
      </div>
      <div className="flex flex-wrap gap-2 mt-5">
        {cardEnabled && sub.can_pay_now && <Button size="sm" className="bg-[#00247D] hover:bg-[#001A5C]" disabled={!!busy} onClick={() => act('pay', () => startHostedPayment(`/billing/subscriptions/${sub.id}/pay-now`))} data-testid={`${t}-pay-now-btn`}>{busy === 'pay' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1.5" />}Pay {fmtMoney(sub.amount, sub.currency)} now</Button>}
        {cardEnabled && sub.can_update_card && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act('card', () => startHostedPayment(`/billing/subscriptions/${sub.id}/update-card`))} data-testid={`${t}-update-card-btn`}>{busy === 'card' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1.5" />}Update card</Button>}
        {sub.can_resume && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => toggle(true)} data-testid={`${t}-resume-btn`}><RefreshCw className="w-4 h-4 mr-1.5" />Resume auto-renew</Button>}
        {(sub.can_cancel || sub.paypal_cancel) && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={!!busy} onClick={() => setConfirm(true)} data-testid={`${t}-cancel-btn`}>Cancel auto-renew</Button>}
        {sub.manage_url && <a href={sub.manage_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid={`${t}-paypal-manage-btn`}>Manage in PayPal<ExternalLink className="w-3.5 h-3.5 ml-1.5" /></Button></a>}
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid={`${t}-cancel-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel automatic renewal?</AlertDialogTitle>
            <AlertDialogDescription>You won't be charged again. You keep full access until {fmtDate(sub.current_period_end)}, and your data is kept.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep renewing</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); toggle(false); }} className="bg-red-600 hover:bg-red-700" data-testid={`${t}-cancel-confirm`}>
              {busy === 'cancel' && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Cancel auto-renew
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
